import React from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';

function CommonModal({ show, setShow, modalContent, modalSize }) {
    const { isDarkMode } = useDarkMode();
    const handleClose = () => setShow(false);

    if (!show) return null;

    const sizeClasses = {
        's': 'max-w-sm',
        'md': 'max-w-md',
        'lg': 'max-w-lg',
        'xl': 'max-w-xl',
        '2xl': 'max-w-2xl',
    };

    const buttonColors = {
        'primary': 'bg-blue-600 hover:bg-blue-700 text-white',
        'secondary': 'bg-gray-600 hover:bg-gray-700 text-white',
        'danger': 'bg-red-600 hover:bg-red-700 text-white',
        'success': 'bg-green-600 hover:bg-green-700 text-white',
        'warning': 'bg-yellow-600 hover:bg-yellow-700 text-white',
    };

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={handleClose}
        >
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
            <div 
                className={`relative w-full ${sizeClasses[modalSize] || sizeClasses['md']} rounded-2xl shadow-xl ${
                    isDarkMode ? 'bg-slate-800' : 'bg-white'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`flex items-center justify-between p-6 border-b ${
                    isDarkMode ? 'border-slate-700' : 'border-gray-200'
                }`}>
                    <h3 className={`text-lg font-semibold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                        {modalContent.heading}
                    </h3>
                    <button
                        onClick={handleClose}
                        disabled={modalContent.isLoading}
                        className={`rounded-lg p-1 hover:bg-opacity-10 transition-colors disabled:opacity-50 ${
                            isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-900'
                        }`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6">
                    {modalContent.body}
                </div>
                
                {/* Component */}
                {modalContent.component && (
                    <div className="px-6">
                        {modalContent.component}
                    </div>
                )}

                {/* Footer */}
                <div className={`flex items-center justify-end gap-3 p-6 border-t ${
                    isDarkMode ? 'border-slate-700' : 'border-gray-200'
                }`}>
                    <button
                        onClick={handleClose}
                        disabled={modalContent.isLoading}
                        className="px-4 py-2 rounded-lg font-medium transition-colors bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Close
                    </button>
                    {modalContent.action && (
                        <button
                            onClick={modalContent.action}
                            disabled={modalContent.isLoading || (modalContent.disabledCondition ? modalContent.disabledCondition : false)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                buttonColors[modalContent.buttonColour] || buttonColors['primary']
                            }`}
                        >
                            {modalContent.isLoading && (
                                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                            )}
                            {modalContent.buttonName}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

CommonModal.propTypes = {
    show: PropTypes.bool.isRequired,
    setShow: PropTypes.func.isRequired,
    modalContent: PropTypes.shape({
        heading: PropTypes.string.isRequired,
        body: PropTypes.node,
        component: PropTypes.node,
        action: PropTypes.func,
        buttonName: PropTypes.string,
        buttonColour: PropTypes.string,
        isLoading: PropTypes.bool,
        disabledCondition: PropTypes.bool,
    }).isRequired,
    modalSize: PropTypes.string,
};

export default CommonModal;